import { PUBLIC_BOTS_LOADING_ID } from "testIds";
import useMedia from "utils/hooks/useMedia";
import React, { useContext, useEffect, useState } from "react";
import PublicBotCard from "features/bots/PublicBots/PublicBotCard";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import styled, { useTheme } from "styled-components";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import usePublicBots from "@shared/features/bots/usePublicBots";
import { useBottomNavigator, useHeaderSearchValue } from "utils/hooks/misc";
import BotsPageWrapper from "features/bots/BotsPageWrapper";
import { openMessage } from "utils/misc";
import MyBotsPageProvider from "features/bots/MyBotsPageProvider";
import { BotTemplateData, BotType } from "@shared/types";
import FlatList from "common/Lists/FlatList";
import EmptyPublicBots from "features/bots/PublicBots/EmptyPublicBots.tsx";
import NewLookPublicBotsLoading from "features/bots/PublicBots/NewLookPublicBotsLoading.tsx";

const PublicBots = () => {
  const appTheme = useTheme();
  const { analytics } = useContext(AnalyticsContext);
  const useFiltersChips = useMedia(["(max-width: 1860px)"], [true], false);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const [showFilters, setShowFilters] = useState(!useFiltersChips);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    analytics?.dcaBotExplorePublicBotsViewWeb({});
  }, [analytics]);

  const numCardsInRow = useMedia(
    ["(max-width: 750px)", "(max-width: 1300px)", "(max-width: 2300px)"],
    [1, 2, 3],
    4
  );

  const headerSearch = useHeaderSearchValue(searchValue);
  const {
    isLoading,
    isFetching,
    filteredBots,
    emptyData,
    emptyFilters,
    refetch,
  } = usePublicBots(headerSearch, setLoading);

  const isInitialLoading = isLoading || (isFetching && !filteredBots.length);
  const emptyState = !isFetching && emptyData && (
    <EmptyPublicBots
      searchValue={searchValue}
      openFilters={() => setShowFilters?.(true)}
    />
  );

  const renderContent = ({
    item,
    index,
  }: {
    item: BotTemplateData;
    index: number;
  }) => {
    return (
      <PublicBotCard
        botData={item}
        key={index}
        id={`public-bot-${index + 1}`}
        botRank={index}
      />
    );
  };

  const mobileCards = (
    <FlatList
      data={filteredBots}
      loading={isLoading}
      renderContent={renderContent}
      onRefresh={refetch}
      refreshing={isFetching}
      emptyState={emptyState}
      hasActionButton={true}
    />
  );

  if (isInitialLoading && appTheme.isNewLook) {
    return (
      <NewLookPublicBotsLoading
        isBottomNavigator={isBottomNavigator}
        useFiltersChips={useFiltersChips}
      />
    );
  }

  if (isInitialLoading) {
    return (
      <DataWrapper aria-busy="true" data-testid={PUBLIC_BOTS_LOADING_ID}>
        <LoadingSpinnerOverlay
          isLoading={true}
          spinnerMarginTop={80}
          spinnerSize="large"
        />
      </DataWrapper>
    );
  }

  return (
    <MyBotsPageProvider
      openMessage={openMessage}
      botType={BotType.ASAP}
      searchValue={searchValue}
      isPublicBots={true}
    >
      <BotsPageWrapper
        setSearchValue={setSearchValue}
        loading={loading}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        useFiltersChips={useFiltersChips}
      >
        <DataWrapper id="public-bots">
          {isBottomNavigator ? (
            mobileCards
          ) : (
            <LoadingSpinnerOverlay
              isLoading={isFetching}
              spinnerMarginTop={50}
              spinnerSize="large"
              backgroundColor={appTheme.colors.layoutBgColor}
            >
              {(filteredBots?.length || 0) > 0 &&
                !emptyFilters &&
                appTheme.isNewLook && (
                  <NewLookGrid>
                    {filteredBots?.map((item, index) =>
                      renderContent({ item, index })
                    )}
                  </NewLookGrid>
                )}
              {(filteredBots?.length || 0) > 0 &&
                !emptyFilters &&
                !appTheme.isNewLook && (
                  <EqualColumnsGrid
                    numColumns={numCardsInRow}
                    style={{ width: "100%" }}
                  >
                    {!emptyFilters &&
                      filteredBots?.map((item, index) =>
                        renderContent({ item, index })
                      )}
                  </EqualColumnsGrid>
                )}
              {emptyState}
            </LoadingSpinnerOverlay>
          )}
        </DataWrapper>
      </BotsPageWrapper>
    </MyBotsPageProvider>
  );
};

export default PublicBots;

const NewLookGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 340px), 1fr));
  align-content: start;
  gap: 16px;
`;
const DataWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;
  position: relative;
  overflow: visible;
  min-height: 200px;
  height: 100%;
`;
